// src/SideWinder.Web/src/components/fof/fofBacktestModel.ts
// Pure helpers for the FOF Backtest pane (spec 2026-10-12 §4.11). Type-only imports.
import type { FofBacktestReport, FofFundEligibility, FofMechanismView, FofTableMechanism, FofTableResult, PotRow } from '../../api/client'

export interface RowChange { fundId: string; field: 'on' | 'weight' | 'tier' | 'reserve'; from: string; to: string }

export function rowDiff(before: PotRow[], after: PotRow[]): RowChange[] {
  const funds = [...new Set([...before, ...after].map(r => r.fundId))]
  const out: RowChange[] = []
  const blank = (f: string): PotRow => ({ fundId: f, enabled: false, weightPct: 0, tier: null, reservePct: 0 })
  for (const f of funds) {
    const a = before.find(r => r.fundId === f) ?? blank(f)
    const b = after.find(r => r.fundId === f) ?? blank(f)
    if (a.enabled !== b.enabled) out.push({ fundId: f, field: 'on', from: a.enabled ? 'on' : 'off', to: b.enabled ? 'on' : 'off' })
    if (a.weightPct !== b.weightPct) out.push({ fundId: f, field: 'weight', from: `${a.weightPct}`, to: `${b.weightPct}` })
    if (a.tier !== b.tier) out.push({ fundId: f, field: 'tier', from: `${a.tier ?? '—'}`, to: `${b.tier ?? '—'}` })
    if (a.reservePct !== b.reservePct) out.push({ fundId: f, field: 'reserve', from: `${a.reservePct}`, to: `${b.reservePct}` })
  }
  return out
}

export function failedGates(t: FofTableResult): string[] {
  const out: string[] = []
  if (!t.gates.dsr) out.push('Deflated Sharpe')
  if (!t.gates.cpcv) out.push('CPCV 10/10')
  if (!t.gates.paired) out.push('Paired vs live')
  if (t.gates.pbo === false) out.push('PBO')
  return out
}

export function rankedTables(rep: FofBacktestReport): FofTableResult[] {
  return [...rep.tables].sort((a, b) => (a.error ? 1 : 0) - (b.error ? 1 : 0) || a.rank - b.rank)
}

/** OAT cells for one fund: field 'w' weight, 't' tier, 'r' reserve. */
export function sensitivitySeries(rep: FofBacktestReport, fund: string, field: 'w' | 't' | 'r') {
  const prefix = `oat:${fund}:${field}`
  return rep.tables
    .filter(t => t.id.startsWith(prefix) && !t.error)
    .map(t => ({ x: Number(t.id.slice(prefix.length)), cagr: t.oos.cagrPct, sharpe: t.cpcvMedianSharpe }))
    .sort((a, b) => a.x - b.x)
}

export function candidateFromLive(rows: PotRow[]): PotRow[] {
  return rows.map(r => ({ ...r }))
}

export function changeLabel(t: FofTableResult): string {
  if (t.kind === 'Live') return 'Live'
  if (t.kind === 'Candidate') return 'Candidate'
  return t.label
}

/** 2026-10-14: inputs that end before the span — they never shorten it, so the end date alone hides them, but
 * every later day reads them as zero (a stalled step-aside series decides on a fake 0 move). Null when none. */
export function laggingInputsText(rep: FofBacktestReport): string | null {
  const lag = rep.spanLimit?.lagging ?? []
  if (!lag.length) return null
  const files = lag.map(l => `${l.file} (${l.fundId}, ends ${l.lastDate})`).join('; ')
  return `Inputs ending before ${rep.spanEnd}: ${files}. Days after each read as zero or blank in the backtest.`
}

/** 2026-10-14: one line saying where the history ends and which fund (and file) stops it. Null on older reports. */
export function spanLimitText(rep: FofBacktestReport): string | null {
  const lim = rep.spanLimit
  if (!lim) return null
  const who = lim.limitedBy.map(f => {
    const files = lim.inputs.filter(i => i.fundId === f).map(i => i.file)
    return files.length ? `${f} (${files.join(', ')})` : f
  })
  const by = who.length ? `, limited by ${who.join(' and ')}` : ''
  if (!lim.stale) return `History ends ${rep.spanEnd}${by}.`
  return `History ends ${rep.spanEnd}, ${lim.weekdaysBehind} weekdays behind${by}. ` +
    "The daily 18:30 UK refresher should catch it up — if this persists, check signal_hub's 'Backtest inputs' log."
}

/** 2026-10-31: a fund's promote eligibility, or null when the mechanism read is missing. */
export function eligibilityOf(m: FofMechanismView | null | undefined, fundId: string): FofFundEligibility | null {
  return m?.funds.find(f => f.fundId === fundId) ?? null
}

/** 2026-10-31: a table's edge-source split and blocks, or null when the mechanism read is missing. */
export function mechanismOf(m: FofMechanismView | null | undefined, tableId: string): FofTableMechanism | null {
  return m?.tables.find(t => t.tableId === tableId) ?? null
}

/** Signed % per year, one decimal: "+1.2", "−0.4", "0.0". */
export function signedPct(v: number): string {
  const r = Math.round(v * 10) / 10
  return r > 0 ? `+${r.toFixed(1)}` : r < 0 ? `−${Math.abs(r).toFixed(1)}` : '0.0'
}

/** One line naming each fund the table raises without its stale-pricing edge; null when none. */
export function blocksText(t: FofTableMechanism | null): string | null {
  if (!t || t.blocks.length === 0) return null
  return `Raises ${t.blocks.map(b => `${b.fundId} (${b.reason})`).join(', ')}.`
}
